import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { APP_PATHS, SERVICE_REQUEST_TABS } from '@/constant/app-paths';
import { LINKED_RECORD_PARAM, LINKED_TAB_PARAM } from '@/hooks/use-linked-record';
import { useNocTypes } from '../../noc/hooks/use-noc';
import { formatMomentDay } from '../../shared/application-utils';
import { StatusBadge } from '../../shared/components/status-badge';
import { useCurrentUserId } from '../../shared/employee';
import { AnyApplication, RequestTypeConfig } from '../../shared/request-types';

type MyRequestRow = { config: RequestTypeConfig; item: AnyApplication };

const appliedTime = (row: MyRequestRow) => new Date(row.item.AppliedDateUtc ?? 0).getTime();

/**
 * My Requests, all types at once (HR Module Prototype › Service Requests › My Requests): one table of
 * the signed-in person's applications of every type they may see, newest first. A row opens it in its
 * own type's list, where its details, Edit and Withdraw are.
 */
export const MyRequestsList = ({ types }: { types: RequestTypeConfig[] }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userId = useCurrentUserId();
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const { data: nocTypes = [] } = useNocTypes();

  const typeKey = types.map((config) => config.type).join(',');
  const {
    data: rows = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['service-requests', 'mine', 'all-types', userId, typeKey],
    queryFn: async (): Promise<MyRequestRow[]> => {
      const perType = await Promise.all(
        types.map(async (config) =>
          (await config.listOwn(userId)).map((item) => ({ config, item }))
        )
      );
      return perType.flat().sort((a, b) => appliedTime(b) - appliedTime(a));
    },
    enabled: Boolean(userId) && types.length > 0,
    // Applications change on the type pages (apply, edit, withdraw): read them again on every visit.
    staleTime: 0,
  });

  const nocTypeName = (id: unknown) => nocTypes.find((type) => type.ItemId === id)?.Name ?? '';
  const open = (row: MyRequestRow) =>
    navigate(
      `${APP_PATHS.SERVICE_REQUESTS}?${LINKED_TAB_PARAM}=${SERVICE_REQUEST_TABS.MY}&type=${row.config.type}&${LINKED_RECORD_PARAM}=${row.item.ItemId}`
    );

  const columns = useMemo<ColumnDef<MyRequestRow>[]>(
    () => [
      {
        id: 'request',
        header: t('REQUEST'),
        enableSorting: false,
        cell: ({ row }) => {
          const { config, item } = row.original;
          const TypeIcon = config.icon;
          const summary = config.summary(item, nocTypeName);
          return (
            <div className="flex min-w-0 items-center gap-3">
              <span className="grid size-8 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
                <TypeIcon className="size-4" />
              </span>
              <div className="flex min-w-0 flex-col">
                <span className="truncate font-medium">{t(config.label)}</span>
                {summary && (
                  <span className="truncate text-xs text-muted-foreground">{summary}</span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: 'requested',
        header: t('REQUESTED'),
        enableSorting: false,
        cell: ({ row }) => (
          <span className="whitespace-nowrap">
            {formatMomentDay(row.original.item.AppliedDateUtc, row.original.item.Timezone)}
          </span>
        ),
      },
      {
        id: 'reference',
        header: t('REFERENCE'),
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {row.original.config.reference?.(row.original.item) || '—'}
          </span>
        ),
      },
      {
        id: 'status',
        header: t('STATUS'),
        enableSorting: false,
        cell: ({ row }) => <StatusBadge status={row.original.item.Status} />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, nocTypes]
  );

  return (
    <AdvanceDataTable
      data={rows}
      columns={columns}
      isLoading={isLoading}
      error={error instanceof Error ? error : null}
      getRowId={(row) => `${row.config.type}-${row.item.ItemId}`}
      pagination={{ ...pagination, totalCount: rows.length }}
      onPaginationChange={setPagination}
      isExpandRowContent={false}
      onRowClick={open}
      mobileView="cards"
    />
  );
};
